import { DateEvent } from "../../../types/date";
import dayjs from "dayjs";
import { JSX, Show, createMemo, splitProps } from "solid-js";
import { TranslationField } from "~/components/TranslationField";
import { mdiCalendarPlus, mdiChevronRight } from "@mdi/js";
import { addEventToCalendar } from "../../../utils/calendar";
import { useDateWidget } from "../../../providers/dates/context";
import { TimeElement } from "../TimeElement";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { cn } from "~/util/utils";
import { Button } from "~/components/ui/button";
import { Icon } from "~/components/ui/icon";

interface DateCardProps extends JSX.HTMLAttributes<HTMLDivElement> {
	event: DateEvent;
	enableTime?: boolean;
	showStartOnly?: boolean;
}

export function EventCard(props: DateCardProps) {
	const [local, other] = splitProps(props, ["class", "event", "enableTime", "showStartOnly"]);
	const { setEvent, formatDate, setOpenContentDialog } = useDateWidget();

	const addCalendarEvent: JSX.EventHandlerUnion<HTMLButtonElement, MouseEvent> = async (event) => {
		event.stopPropagation();
		await addEventToCalendar(props.event);
	};

	const selectEvent = () => {
		if(props.event.content) {
			setEvent(props.event);
			setOpenContentDialog(true);
		}
	};

	const hasTime = createMemo(() => {

		const { 
			isFullDay, 
			startTime, 
			endTime 
		} = local.event;

		if (isFullDay) {
			return false;
		}

		const start = dayjs(startTime);
		const end = dayjs(endTime);

		if (start.isSame(end)) {
			return false;
		}

		return !local.showStartOnly;
	});

	return (
		<Card
			class={cn(
				"relative transition duration-200 shadow-md border-l-4 self-start",
				local.event.content && "cursor-pointer hover:bg-gray-50 active:bg-gray-100",
				local.class
			)}
			tabIndex={local.event.content ? 0 : undefined}
			onClick={local.event.content ? selectEvent : undefined}
			role={local.event.content ? "button" : undefined}
			style={{ "border-left-color": local.event.category.color }}
			{...other}
		>
			<CardHeader
				class="p-5"
			>
				<CardTitle class="font-medium">
					{formatDate(props.event)}
				</CardTitle>
				<CardDescription>
					<TranslationField key={local.event.title} />
				</CardDescription>
			</CardHeader>
			<Show when={hasTime()}>
				<CardContent
					class="p-5 pt-0"
				>
					<TimeElement event={local.event} />
					<Show when={local.event.content}>
						<Icon
							class="absolute right-4 bottom-0 top-0 my-auto"
							size="md"
							path={mdiChevronRight}
						/>
					</Show>
				</CardContent>
			</Show>
			<Button
				class="absolute -top-4 -right-4 size-10 rounded-full bg-white hover:bg-accent/10"
				variant="outline"
				size="sm"
				onClick={addCalendarEvent}
			>
				<Icon path={mdiCalendarPlus} color="gray" />
			</Button>
		</Card>
	);
}